import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';

/**
 * Helpers shared by the five Operations screens. Nothing here renders — the
 * screens own their markup; this file owns the plumbing they all repeat.
 */

/** Surface the API's own message (§3 envelope) rather than a generic failure. */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

export interface BranchOption {
  id: string;
  name: string;
}

/**
 * D-05 — `GET /branches` is gated on `branches.view`, NOT `settings.view`. The
 * backend split that key out (I-10) precisely so reading branch identity does
 * not require the settings permission operations staff correctly do not hold:
 * Cashier, Waiter, Head Waiter, Shift Manager and Kitchen Manager all carry
 * `branches.view` and none carries `settings.view`. Gating on the wrong key
 * made the branch filter vanish for every non-admin on all seven Operations
 * screens. The fallback stands for the genuinely unpermitted case: the filter
 * disappears rather than the screen failing, because the server still scopes
 * every read to what the caller may see.
 */
export function useBranches(): BranchOption[] {
  const { can } = usePermission();
  const allowed = can('branches.view');
  const [branches, setBranches] = useState<BranchOption[]>([]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    api
      .get('/branches', { params: { limit: 200 } })
      .then(({ data }) => {
        if (!cancelled) setBranches(rowsOf<BranchOption>(data).rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  return branches;
}

/**
 * The branch rooms a *queue* screen has to be listening in, which is not the
 * same set as the branches its current rows came from.
 *
 * Deriving the rooms from the loaded rows — `for (const row of requests) ids.add(row.branchId)`
 * — deadlocks on the state that matters most: an empty queue joins no room, so
 * the event announcing the first request of the day is never delivered, and the
 * row appears only when someone reaches for the refresh button. Having a row is
 * the precondition for receiving the event that would give you the row.
 *
 * It is also wrong while non-empty. With no branch filter set the screen claims
 * to show every branch the actor may see, but only hears from the ones that
 * happen to be represented already — a first request in a quiet branch is
 * missed for the same reason. And because the set changes identity as rows
 * arrive, the subscription tears down and re-joins on every list change, which
 * is a window where events land in no listener at all.
 *
 * So: the branches the actor can *see*, which is what the screen is about. The
 * selected branch and the rows' own branches are unioned in rather than
 * replaced — `useBranches` returns nothing to an actor without `branches.view`,
 * and that actor still has rows and still needs them to update live.
 *
 * `useRealtime` re-resolves on the joined string, so a stable set of ids
 * arriving in a different order does not churn the subscription.
 */
export function useBranchRooms(
  selectedBranchId: string | null | undefined,
  rows: readonly { branchId?: string | null }[],
): string[] {
  const branches = useBranches();
  const key = [
    selectedBranchId ?? '',
    branches.map((branch) => branch.id).join(','),
    rows.map((row) => row.branchId ?? '').join(','),
  ].join('|');

  return useMemo(() => {
    const ids = new Set<string>();
    if (selectedBranchId) ids.add(selectedBranchId);
    for (const branch of branches) ids.add(branch.id);
    for (const row of rows) if (row.branchId) ids.add(row.branchId);
    return [...ids].sort().map((id) => `branch:${id}`);
    // Contents, not array identity: both `branches` and `rows` are fresh arrays
    // on every render, and depending on them re-joins every room each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/**
 * A clock that re-renders its host on an interval, so elapsed timers tick
 * without refetching. Returns the current epoch milliseconds.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * Money for the Operations screens, in the currency the row is actually
 * denominated in.
 *
 * D-04 — this replaces `formatAmount`, which claimed in its own doc comment
 * never to re-parse money and then did exactly that: `Number(value)` at a
 * hardcoded two decimals with no `style: 'currency'`, so an inventory valuation
 * rendered as a bare `1,250.00` with no symbol or code, and a KWD figure lost a
 * fil on the way. `formatCurrency` in `@/lib/i18n` reads the ISO minor unit
 * from `decimalsFor` and takes the decimal string straight through, so the only
 * correct implementation is to delegate to it — the pattern `Procurement.tsx`
 * already uses.
 *
 * `currency` is required on purpose. A purchase document, a check and a branch
 * all carry their own code; an amount whose currency is unknown is a bug to
 * surface, not a `SAR` to assume.
 */
export function formatMoney(value: unknown, currency: string, locale: string): string {
  if (value === null || value === undefined || value === '') return '—';
  return formatCurrency(
    typeof value === 'number' ? value : String(value),
    currency,
    locale,
  );
}

/** Quantities carry up to 3dp; trailing zeros are noise on a board. */
export function formatQuantity(value: unknown): string {
  const text = String(value ?? '').trim();
  if (!text) return '0';
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function formatTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
}

/** `mm:ss` below an hour, `h:mm:ss` above it. */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** `yyyy-mm-dd` in the viewer's own timezone, which is what `<input type=date>` speaks. */
export function toDayInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

export function startOfDayIso(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString();
}

export function endOfDayIso(day: string): string {
  return new Date(`${day}T23:59:59.999`).toISOString();
}

/** Drops empty strings so a blank filter never reaches the API as `?status=`. */
export function useQueryParams(source: Record<string, unknown>): Record<string, unknown> {
  const key = JSON.stringify(source);
  return useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(JSON.parse(key) as Record<string, unknown>)) {
      if (value === '' || value === null || value === undefined) continue;
      out[name] = value;
    }
    return out;
    // `source` is compared by value through `key`
  }, [key]);
}
